import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, Edit2, Trash2, Camera, UserCircle, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const EmployeeService = {
  getEmployees: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/employees`, {
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  },

  getDeletedEmployees: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/employees/deleted`, {
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching deleted employees:", error);
      throw error;
    }
  },

  restoreDeletedEmployee: async (empNo, companyId, token, userType) => {
    try {
      const response = await axios.post(
        `${API_URL}/employees/deleted/restore`,
        { empNo },
        {
          headers: {
            "user-id": token,
            "user-type": userType,
            "company-id": companyId,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error restoring deleted employee:", error);
      throw error;
    }
  },

  hardDeleteDeletedEmployee: async (deletedId, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/employees/deleted/${deletedId}`, {
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
    } catch (error) {
      console.error("Error permanently deleting deleted employee:", error);
      throw error;
    }
  },

  uploadExcelEmployees: async (excelFile, companyId, token, userType) => {
    try {
      const formData = new FormData();
      formData.append("excelFile", excelFile);

      const response = await axios.post(
        `${API_URL}/employees/upload-excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "user-id": token,
            "user-type": userType,
            "company-id": companyId,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading Excel file:", error);
      throw error;
    }
  },

  addEmployee: async (employee, profilePicture, companyId, token, userType) => {
    try {
      const formData = new FormData();

      Object.keys(employee).forEach((key) => {
        const value = employee[key];
        if (value === null || value === undefined) {
          formData.append(key, "");
        } else {
          formData.append(key, value);
        }
      });

      if (profilePicture) {
        formData.append("profilePicture", profilePicture);
      }

      const response = await axios.post(`${API_URL}/employees`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error adding employee:", error);
      throw error;
    }
  },

  updateEmployee: async (
    id,
    employee,
    profilePicture,
    companyId,
    token,
    userType
  ) => {
    try {
      const formData = new FormData();

      Object.keys(employee).forEach((key) => {
        const value = employee[key];
        if (value === null || value === undefined) {
          formData.append(key, "");
        } else {
          formData.append(key, value);
        }
      });

      if (profilePicture) {
        formData.append("profilePicture", profilePicture);
      }

      const response = await axios.put(
        `${API_URL}/employees/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "user-id": token,
            "user-type": userType,
            "company-id": companyId,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error updating employee:", error);
      throw error;
    }
  },

  uploadProfilePicture: async (id, file, companyId, token, userType) => {
    try {
      const formData = new FormData();
      formData.append("profilePicture", file);

      const response = await axios.post(
        `${API_URL}/employees/${id}/profile-picture`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "user-id": token,
            "user-type": userType,
            "company-id": companyId,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading profile picture:", error);
      throw error;
    }
  },

  updatePassword: async (id, password, companyId, token, userType) => {
    try {
      const response = await axios.put(
        `${API_URL}/employees/${id}/password`,
        { password },
        {
          headers: {
            "user-id": token,
            "user-type": userType,
            "company-id": companyId,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating password:", error);
      throw error;
    }
  },

  deleteEmployee: async (id, deletionReason, companyId, token, userType) => {
    try {
      await axios.delete(`${API_URL}/employees/${id}`, {
        data: { deletionReason },
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
    } catch (error) {
      console.error("Error deleting employee:", error);
      throw error;
    }
  },

  getDepartments: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/departments`, {
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching departments:", error);
      throw error;
    }
  },

  getDesignations: async (companyId, token, userType) => {
    try {
      const response = await axios.get(`${API_URL}/designations`, {
        headers: {
          "user-id": token,
          "user-type": userType,
          "company-id": companyId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching designations:", error);
      throw error;
    }
  },

  getStructure: async (companyId, userId, userType) => {
    try {
      const response = await axios.get(`${API_URL}/employees/structure`, {
        headers: {
          "user-id": userId,
          "user-type": userType,
          "company-id": companyId,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching structure:", error);
      throw error;
    }
  },

  // Get the last employee number and generate the next one
  getLastEmpNo: async (companyId, companyCode, userId, userType) => {
    try {
      console.log('Calling last-empno endpoint with:', { companyId, prefix: companyCode });
      const response = await axios.get(`${API_URL}/employees/last-empno`, {
        headers: {
          "user-id": userId,
          "user-type": userType,
          "company-id": companyId,
        },
        params: { 
          prefix: companyCode 
        },
      });
      
      console.log('last-empno response:', response.data);
      
      if (!response.data || !response.data.lastEmpNo) {
        console.error('Invalid response format from last-empno endpoint');
        return null;
      }
      
      return response.data.lastEmpNo;
    } catch (error) {
      console.error("Error in getLastEmpNo:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      return null;
    }
  },
};

const CreateEmployeeAccount = () => {
  const { user, userType, companyId, logout } = useAuth();
  const [companyCode, setCompanyCode] = useState("");
  const [empNoLoading, setEmpNoLoading] = useState(false);
  const [empNoError, setEmpNoError] = useState(null);
  const [showEmployeeForm, setShowEmployeeForm] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 640;
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [empNoNumeric, setEmpNoNumeric] = useState("");
  const [reportsToSearch, setReportsToSearch] = useState("");
  const [showReportsToDropdown, setShowReportsToDropdown] = useState(false);
  const [regenerateEmpNoKey, setRegenerateEmpNoKey] = useState(0);
  const [employeeListMode, setEmployeeListMode] = useState("active");
  const [deletedEmployees, setDeletedEmployees] = useState([]);
  const [formData, setFormData] = useState({
    email: "",
    reportsTo: "",
    empNo: "",
    name: "",
    mobile: "",
    department: "",
    departmentName: "",
    designation: "",
    joinDate: "",
    salary: "",
    epfNumber: "",
    address: "",
    gender: "",
    maritalStatus: "",
    nationalityCode: "",
    bank: "",
    bankAccNo: "",
    birthday: "",
    nicNumber: "",
    company_id: companyId,
  });

  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDesignation, setSelectedDesignation] = useState("");
  const navigate = useNavigate();
  const [excelFile, setExcelFile] = useState(null);
  const [excelUploadStatus, setExcelUploadStatus] = useState({
    loading: false,
    message: "",
  });
  const excelFileInputRef = useRef(null);
  const [structureData, setStructureData] = useState({
    designations: [],
    employees: [],
    hierarchyOrder: [],
  });

  useEffect(() => {
    if (editMode) {
      setShowEmployeeForm(true);
    }
  }, [editMode]);

  useEffect(() => {
    if (!companyId) {
      logout();
      navigate("/");
      return;
    }

    setFormData((prev) => ({ ...prev, company_id: companyId }));

    fetchEmployees();
    fetchDeletedEmployees();
    fetchDepartments();
    fetchDesignations();
    fetchStructureData();
    fetchCompanyCodeAndAutoEmpNo();
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    if (employeeListMode === "deleted") {
      fetchDeletedEmployees();
    } else {
      fetchEmployees();
    }
  }, [companyId, employeeListMode]);

  useEffect(() => {
    if (!companyId) return;
    if (editMode) return;
    fetchCompanyCodeAndAutoEmpNo();
  }, [companyId, editMode, regenerateEmpNoKey]);

  useEffect(() => {
    if (!editMode) return;
    if (!companyCode) return;

    const currentEmpNo = String(formData.empNo || "");
    const match = currentEmpNo.match(new RegExp(`^${companyCode}(\\d{4})$`));
    if (match && match[1]) {
      setEmpNoNumeric(match[1]);
      return;
    }

    // If employee number is temporary (e.g. EMPxxxx) or doesn't match company code,
    // suggest the next available company-code based number.
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const storedUserType = localStorage.getItem("userType");
        const next = await EmployeeService.getLastEmpNo(
          companyId,
          companyCode,
          user?.id,
          storedUserType
        );
        const nextMatch = String(next || "").match(new RegExp(`^${companyCode}(\\d{4})$`));
        if (nextMatch && nextMatch[1]) {
          setEmpNoNumeric(nextMatch[1]);
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [editMode, companyCode, companyId, formData.empNo, user?.id]);

  const fetchCompanyCodeAndAutoEmpNo = async () => {
    console.log('fetchCompanyCodeAndAutoEmpNo called');
    if (editMode) {
      console.log('Edit mode - skipping auto generation');
      return;
    }
    
    setEmpNoError(null);
    setEmpNoLoading(true);
    
    try {
      console.log('Fetching company data...');
      const response = await axios.get(`${API_URL}/companies`);
      console.log('Companies response:', response.data);
      
      const company = response.data.find(c => String(c.id) === String(companyId));
      console.log('Found company:', company);
      
      const code = company?.company_code || "";
      console.log('Company code:', code);
      
      setCompanyCode(code);
      
      if (code) {
        console.log('Getting last employee number for code:', code);
        const token = localStorage.getItem("token");
        const userType = localStorage.getItem("userType");
        
        console.log('Calling getLastEmpNo with:', { companyId, code, userId: user?.id, userType });
        const result = await EmployeeService.getLastEmpNo(
          companyId, 
          code, 
          user?.id, 
          userType
        );
        
        console.log('getLastEmpNo result:', result);
        
        if (result) {
          console.log('Setting employee number to:', result);
          setFormData(prev => ({ ...prev, empNo: result }));
        } else {
          console.log('No result from getLastEmpNo');
        }
      } else {
        console.log('No company code found');
      }
    } catch (err) {
      console.error("Error in fetchCompanyCodeAndAutoEmpNo:", err);
      console.error("Error details:", err.response?.data || err.message);
      setEmpNoError("Failed to auto-generate employee number. Please enter it manually.");
    } finally {
      console.log('Finished fetchCompanyCodeAndAutoEmpNo');
      setEmpNoLoading(false);
    }
  };

  const fetchStructureData = async () => {
    try {
      const data = await EmployeeService.getStructure(companyId, user.id, userType);
      setStructureData(data);
    } catch (error) {
      setError("Error fetching structure data: " + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      email: "",
      empNo: "",
      name: "",
      mobile: "",
      department: "",
      departmentName: "",
      designation: "",
      joinDate: "",
      salary: "",
      epfNumber: "",
      address: "",
      gender: "",
      maritalStatus: "",
      nationalityCode: "",
      bank: "",
      bankAccNo: "",
      reportsTo: "",
      birthday: "",
      nicNumber: "",
      company_id: companyId,
    });
    setPassword("");
    setConfirmPassword("");
    setEmpNoNumeric("");
    setReportsToSearch("");
    setShowReportsToDropdown(false);
    setProfilePicture(null);
    setProfilePicturePreview(null);
    setEditMode(false);
    setEditId(null);
    setError(null);
    setSuccess(null);
    setRegenerateEmpNoKey((k) => k + 1);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const fetchDesignations = async () => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const designationList = await EmployeeService.getDesignations(
        companyId,
        token,
        userType
      );
      designationList.sort((a, b) => a.hierarchyLevel - b.hierarchyLevel);
      setDesignations(designationList);
    } catch (error) {
      setError("Error fetching designations: " + error.message);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const departmentList = await EmployeeService.getDepartments(
        companyId,
        token,
        userType
      );
      setDepartments(departmentList);
    } catch (error) {
      setError("Error fetching departments: " + error.message);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const employeeList = await EmployeeService.getEmployees(
        companyId,
        token,
        userType
      );
      setEmployees(employeeList);
    } catch (error) {
      setError("Error fetching employees: " + error.message);
    }
  };

  const fetchDeletedEmployees = async () => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const list = await EmployeeService.getDeletedEmployees(companyId, token, userType);
      setDeletedEmployees(list || []);
    } catch (error) {
      setError("Error fetching deleted employees: " + error.message);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "department") {
      const selectedDepartment = departments.find(
        (dept) => dept.id === parseInt(value)
      );
      setFormData({
        ...formData,
        department: value,
        departmentName: selectedDepartment ? selectedDepartment.dname : "",
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validImageTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!validImageTypes.includes(file.type)) {
        setError("Please select a valid image file (JPEG, PNG, GIF, or WEBP)");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Profile picture must be less than 5MB");
        return;
      }

      setProfilePicture(file);

      const reader = new FileReader();
      reader.onload = () => {
        setProfilePicturePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setError(null);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleEdit = (employee) => {
    setEditMode(true);
    setEditId(employee.id);
    setPassword("");
    setConfirmPassword("");
    setShowReportsToDropdown(false);

    const formattedJoinDate = employee.joinDate
      ? formatDateForInput(employee.joinDate)
      : "";
    const formattedBirthday = employee.birthday
      ? formatDateForInput(employee.birthday)
      : "";

    setFormData({
      email: employee.email || "",
      empNo: employee.empNo || "",
      name: employee.name || "",
      mobile: employee.mobile || "",
      department: employee.department || "",
      departmentName: employee.departmentName || "",
      designation: employee.designation || "",
      joinDate: formattedJoinDate,
      salary: employee.salary || "",
      epfNumber: employee.epfNumber || "",
      address: employee.address || "",
      gender: employee.gender || "",
      maritalStatus: employee.maritalStatus || "",
      nationalityCode: employee.nationalityCode || "",
      bank: employee.bank || "",
      bankAccNo: employee.bankAccNo || "",
      reportsTo: employee.reportsTo || "",
      birthday: formattedBirthday,
      nicNumber: employee.nicNumber || "",
      company_id: companyId,
    });

    // Pre-fill searchable supervisor input
    if (employee.reportsTo) {
      const sup = employees.find((e) => String(e.id) === String(employee.reportsTo));
      setReportsToSearch(sup?.name ? `${sup.name}` : "");
    } else {
      setReportsToSearch("");
    }

    if (employee.profilePictureUrl) {
      setProfilePicturePreview(employee.profilePictureUrl);
    } else {
      setProfilePicturePreview(null);
    }

    setProfilePicture(null);
    setError(null);
    setSuccess(null);
  };

  const formatDateForInput = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return "—";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "—";
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "—";
    }
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      const deletionReason = window.prompt("Enter deletion reason:");
      if (!deletionReason || String(deletionReason).trim() === "") {
        setError("Deletion reason is required.");
        return;
      }
      try {
        const token = localStorage.getItem("token");
        const userType = localStorage.getItem("userType");
        await EmployeeService.deleteEmployee(
          employeeId,
          deletionReason,
          companyId,
          token,
          userType
        );
        setSuccess("Employee deleted successfully!");
        fetchEmployees();
        fetchDeletedEmployees();
        resetForm();
      } catch (error) {
        setError("Error deleting employee: " + error.message);
      }
    }
  };

  const handleRestoreDeletedEmployee = async (empNo) => {
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const restored = await EmployeeService.restoreDeletedEmployee(empNo, companyId, token, userType);
      setSuccess(`Employee restored successfully! Employee No: ${restored?.empNo || empNo}`);
      setEmployeeListMode("active");
      await fetchEmployees();
      await fetchDeletedEmployees();
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message ||
        "Failed to restore employee.";
      setError(msg);
    }
  };

  const handleHardDeleteDeletedEmployee = async (deletedId) => {
    if (!window.confirm("Permanently delete this archived employee record? This cannot be undone.")) {
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      await EmployeeService.hardDeleteDeletedEmployee(deletedId, companyId, token, userType);
      setSuccess("Deleted employee record removed permanently.");
      await fetchDeletedEmployees();
    } catch (error) {
      setError("Failed to permanently delete deleted employee record.");
    }
  };

  const handleAddDeletedEmployee = async () => {
    const empNo = window.prompt("Enter old employee number to restore (e.g., VCM0002):");
    if (!empNo || String(empNo).trim() === "") return;
    await handleRestoreDeletedEmployee(String(empNo).trim());
  };

  const validateForm = () => {
    const missing = [];

    if (!formData.empNo || String(formData.empNo).trim() === "") missing.push("Employee Number");
    if (!formData.email || String(formData.email).trim() === "") missing.push("Email");
    if (!formData.name || String(formData.name).trim() === "") missing.push("Name");
    if (!formData.department || String(formData.department).trim() === "") missing.push("Department");
    if (!formData.designation || String(formData.designation).trim() === "") missing.push("Designation/Role");
    if (!formData.joinDate || String(formData.joinDate).trim() === "") missing.push("Join Date");

    if (editMode && companyCode) {
      const numeric = String(empNoNumeric || "").trim();
      if (!/^[0-9]{4}$/.test(numeric)) {
        missing.push("Employee Number (4 digits)");
      }
    }

    if (missing.length > 0) {
      return { required: `Please fill the following field(s): ${missing.join(", ")}` };
    }

    if (!editMode) {
      const errors = {};

      if (employees.some((employee) => employee.empNo === formData.empNo)) {
        errors.empNo = "Employee Number already exists.";
      }
      if (
        formData.epfNumber &&
        employees.some((employee) => employee.epfNumber === formData.epfNumber)
      ) {
        errors.epfNumber = "EPF Number already exists.";
      }
      if (
        formData.nicNumber &&
        employees.some((employee) => employee.nicNumber === formData.nicNumber)
      ) {
        errors.nicNumber = "NIC Number already exists.";
      }

      if (Object.keys(errors).length > 0) {
        return errors;
      }
    }

    return null;
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];

      if (!validTypes.includes(file.type)) {
        setError("Please select a valid Excel file (.xls or .xlsx)");
        setExcelFile(null);
        e.target.value = null;
        return;
      }

      setExcelFile(file);
      setError(null);
    }
  };

  const uploadExcelFile = async () => {
    if (!excelFile) {
      setError("Please select an Excel file first");
      return;
    }

    setExcelUploadStatus({ loading: true, message: "Uploading..." });

    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");
      const response = await EmployeeService.uploadExcelEmployees(
        excelFile,
        companyId,
        token,
        userType
      );

      setExcelUploadStatus({
        loading: false,
        message: `Successfully imported ${response.successCount} employees`,
      });

      setSuccess(`Successfully imported ${response.successCount} employees`);

      setExcelFile(null);
      if (excelFileInputRef.current) {
        excelFileInputRef.current.value = null;
      }

      fetchEmployees();
    } catch (error) {
      setExcelUploadStatus({ loading: false, message: "" });
      setError(
        `Failed to import employees: ${
          error.response?.data?.error || error.message
        }`
      );
    }
  };

  const triggerExcelFileInput = () => {
    excelFileInputRef.current.click();
  };

  const downloadExcelTemplate = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/employees/template`,
        {
          responseType: 'blob',
          headers: {
            'user-id': user,
            'user-type': userType,
            'company-id': companyId,
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute("download", "employee_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess("Template downloaded successfully!");
    } catch (error) {
      setError(
        "Failed to download template: " +
          (error.response?.data?.error || error.message)
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password || confirmPassword) {
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
    }

    const validationErrors = validateForm();
    if (validationErrors) {
      setError(Object.values(validationErrors)[0]);
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const userType = localStorage.getItem("userType");

      if (editMode) {
        const normalizedReportsTo = formData.reportsTo === "" ? "" : formData.reportsTo;

        const finalEmpNo = companyCode
          ? `${companyCode}${String(empNoNumeric || "").padStart(4, "0")}`
          : formData.empNo;

        const employeeData = {
          email: formData.email,
          empNo: finalEmpNo,
          name: formData.name,
          mobile: formData.mobile,
          department: formData.department,
          departmentName: formData.departmentName,
          designation: formData.designation,
          joinDate: formData.joinDate,
          salary: formData.salary,
          epfNumber: formData.epfNumber,
          address: formData.address,
          gender: formData.gender,
          maritalStatus: formData.maritalStatus,
          nationalityCode: formData.nationalityCode,
          bank: formData.bank,
          bankAccNo: formData.bankAccNo,
          birthday: formData.birthday,
          reportsTo: normalizedReportsTo,
          nicNumber: formData.nicNumber,
          company_id: companyId,
        };

        await EmployeeService.updateEmployee(
          editId,
          employeeData,
          profilePicture,
          companyId,
          token,
          userType
        );

        if (password) {
          await EmployeeService.updatePassword(
            editId,
            password,
            companyId,
            token,
            userType
          );
        }

        setSuccess(`Employee updated successfully! Employee No: ${employeeData.empNo}`);
      } else {
        const employeeData = {
          email: formData.email,
          empNo: formData.empNo,
          name: formData.name,
          mobile: formData.mobile,
          department: formData.department,
          departmentName: formData.departmentName,
          designation: formData.designation,
          joinDate: formData.joinDate,
          salary: formData.salary,
          epfNumber: formData.epfNumber,
          address: formData.address,
          gender: formData.gender,
          maritalStatus: formData.maritalStatus,
          nationalityCode: formData.nationalityCode,
          bank: formData.bank,
          bankAccNo: formData.bankAccNo,
          birthday: formData.birthday,
          reportsTo: formData.reportsTo === "" ? "" : formData.reportsTo,
          nicNumber: formData.nicNumber,
          userType: "employee",
          company_id: companyId,
        };

        if (password) {
          employeeData.password = password;
        }

        const created = await EmployeeService.addEmployee(
          employeeData,
          profilePicture,
          companyId,
          token,
          userType
        );

        if (created?.empNo && created.empNo !== formData.empNo) {
          setFormData((prev) => ({ ...prev, empNo: created.empNo }));
        }

        const setupMsg = password
          ? "Password set successfully."
          : created?.passwordSetupEmailSent
          ? "Password setup email sent."
          : "Password setup email could not be sent.";

        const displayEmpNo = created?.empNo || employeeData.empNo;
        if (created?.empNoAdjusted) {
          setSuccess(
            `Employee number already used. New employee number picked: ${displayEmpNo} for ${employeeData.name}. ${setupMsg}`
          );
        } else {
          setSuccess(`Employee created successfully! Employee No: ${displayEmpNo}. ${setupMsg}`);
        }
      }

      await fetchEmployees();
      if (!editMode) {
        resetForm();
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message ||
        "Something went wrong. Please try again.";
      setError(msg);
    }
    setIsLoading(false);
  };

  const filteredEmployees = employees.filter((employee) => {
    const matchesSearchTerm =
      employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.empNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (employee.epfNumber &&
        employee.epfNumber.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDepartment =
      !selectedDepartment || employee.department === selectedDepartment;

    const matchesDesignation =
      !selectedDesignation || employee.designation === selectedDesignation;

    return matchesSearchTerm && matchesDepartment && matchesDesignation;
  });

  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-6 text-center">
          {editMode ? "Edit Employee" : "Employees"}
        </h1>

        <div className="sm:hidden flex justify-center mb-4">
          <button
            type="button"
            onClick={() => setShowEmployeeForm((v) => !v)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg"
          >
            {showEmployeeForm ? "Hide Form" : "Add Employee"}
          </button>
        </div>

        {/* Employee Form */}
        <div className={`${showEmployeeForm ? '' : 'hidden'} sm:block`}>
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-lg mb-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-6">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div>
              <h3 className="font-semibold text-lg mb-3 text-gray-700">
                Personal Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm">Employee Number</label>
                  {!editMode ? (
                    <input
                      type="text"
                      name="empNo"
                      value={formData.empNo}
                      onChange={handleInputChange}
                      readOnly
                      className={`border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 cursor-not-allowed`}
                      placeholder="Auto-generated employee number"
                      required
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="px-3 py-3 border rounded-lg bg-gray-100 text-gray-700 select-none">
                        {companyCode || "CODE"}
                      </div>
                      <input
                        type="text"
                        value={empNoNumeric}
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, "").slice(0, 4);
                          setEmpNoNumeric(onlyDigits);
                        }}
                        className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="0001"
                        required
                      />
                    </div>
                  )}
                  {empNoLoading && (
                    <p className="text-sm text-gray-500 mt-1">
                      Generating employee number...
                    </p>
                  )}
                  {empNoError && (
                    <p className="text-sm text-red-500 mt-1">{empNoError}</p>
                  )}
                </div>

                <div>
                  <label className="block mb-1 text-sm">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm">Mobile</label>
                  <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter mobile number"
                  />
                  </div>
                <div>
                  <label className="block mb-1 text-sm">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm">Birthday</label>
                  <input
                    type="date"
                    name="birthday"
                    value={formData.birthday}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">NIC Number</label>
                  <input
                    type="text"
                    name="nicNumber"
                    value={formData.nicNumber}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter NIC number"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-sm">Marital Status</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Marital Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Employment Details */}
            <div>
              <h3 className="font-semibold text-lg mb-3 text-gray-700">
                Employment Details
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm">Department</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleInputChange}
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Department</option>
                    {departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.dname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-sm">Designation/Role</label>
                  <select
                    name="designation"
                    value={formData.designation}
                    onChange={handleInputChange}
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Designation</option>
                    {designations.map((designation) => (
                      <option key={designation.id} value={designation.name}>
                        {designation.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 text-sm">Reports To (Optional)</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={reportsToSearch}
                      onChange={(e) => {
                        setReportsToSearch(e.target.value);
                        setShowReportsToDropdown(true);
                        setFormData((prev) => ({ ...prev, reportsTo: "" }));
                      }}
                      onFocus={() => setShowReportsToDropdown(true)}
                      onBlur={() => {
                        // Allow click selection
                        setTimeout(() => setShowReportsToDropdown(false), 150);
                      }}
                      className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={formData.department ? "Search supervisor by name" : "Select department first"}
                      disabled={!formData.department}
                    />

                    {showReportsToDropdown && formData.department && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setFormData((prev) => ({ ...prev, reportsTo: "" }));
                            setReportsToSearch("");
                            setShowReportsToDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                        >
                          No Supervisor
                        </button>

                        {employees
                          .filter((emp) => String(emp.id) !== String(editId))
                          .filter((emp) => String(emp.department) === String(formData.department))
                          .filter((emp) => {
                            const q = reportsToSearch.toLowerCase().trim();
                            if (!q) return true;
                            return String(emp.name || "").toLowerCase().includes(q);
                          })
                          .slice(0, 30)
                          .map((emp) => (
                            <button
                              key={emp.id}
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, reportsTo: emp.id }));
                                setReportsToSearch(emp.name || "");
                                setShowReportsToDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                            >
                              {emp.name} ({emp.designation})
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm">Join Date</label>
                  <input
                    type="date"
                    name="joinDate"
                    value={formData.joinDate}
                    onChange={handleInputChange}
                    required
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Salary</label>
                  <input
                    type="number"
                    name="salary"
                    value={formData.salary}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter salary"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">EPF Number</label>
                  <input
                    type="text"
                    name="epfNumber"
                    value={formData.epfNumber}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter EPF number"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h3 className="font-semibold text-lg mb-3 text-gray-700">
                Additional Information
              </h3>

              <div className="space-y-4">
                {/* Profile Picture Section */}
                <div className="flex justify-center mb-6">
                  <div className="text-center">
                    <div
                      onClick={triggerFileInput}
                      className="w-32 h-32 rounded-full bg-gray-200 mx-auto mb-3 flex items-center justify-center cursor-pointer overflow-hidden border-4 border-blue-100 hover:border-blue-300 transition-all"
                    >
                      {profilePicturePreview ? (
                        <img
                          src={profilePicturePreview}
                          alt="Profile Preview"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserCircle size={80} className="text-gray-400" />
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={triggerFileInput}
                      className="text-blue-600 flex items-center justify-center gap-1 mx-auto font-medium"
                    >
                      <Camera size={16} />
                      {profilePicturePreview ? "Change Photo" : "Upload Photo"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block mb-1 text-sm">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter address"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Nationality Code</label>
                  <input
                    type="text"
                    name="nationalityCode"
                    value={formData.nationalityCode}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter nationality code"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Bank</label>
                  <input
                    type="text"
                    name="bank"
                    value={formData.bank}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter bank name"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Bank Account Number</label>
                  <input
                    type="text"
                    name="bankAccNo"
                    value={formData.bankAccNo}
                    onChange={handleInputChange}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter bank account number"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={editMode ? "Set new password (optional)" : "Enter password (optional)"}
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm">Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Confirm password"
                    autoComplete="new-password"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 mt-6 sm:flex-row sm:justify-between sm:items-center">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="text-blue-600 hover:text-blue-700 text-left"
            >
              Back to Dashboard
            </button>

            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                ref={excelFileInputRef}
                accept=".xls,.xlsx"
                onChange={handleExcelFileChange}
                className="hidden"
              />

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={downloadExcelTemplate}
                  className="bg-purple-600 text-white font-medium p-2 px-3 rounded-lg hover:bg-purple-700 flex items-center"
                >
                  <Upload size={18} className="mr-1 transform rotate-180" />
                  Download Template
                </button>
                <button
                  type="button"
                  onClick={triggerExcelFileInput}
                  className="bg-green-600 text-white font-medium p-2 px-3 rounded-lg hover:bg-green-700 flex items-center"
                >
                  <Upload size={18} className="mr-1" />
                  Select Excel
                </button>
                {excelFile && (
                  <span className="text-sm text-gray-600">
                    {excelFile.name}
                  </span>
                )}
                {excelFile && (
                  <button
                    type="button"
                    onClick={uploadExcelFile}
                    disabled={excelUploadStatus.loading}
                    className="bg-blue-600 text-white font-medium p-2 px-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed flex items-center"
                  >
                    {excelUploadStatus.loading
                      ? "Uploading..."
                      : "Import Employees"}
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              {editMode && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-500 text-white font-bold p-3 rounded-lg hover:bg-gray-600"
                >
                  Cancel Edit
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                {isLoading
                  ? "Processing..."
                  : editMode
                  ? "Update Employee"
                  : "Create Employee Account"}
              </button>
            </div>
          </div>
          </form>
        </div>

        {/* Employee List Table */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
          <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:justify-between sm:items-center">
            <h2 className="text-2xl font-bold">Employee List</h2>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="radio"
                  name="employeeListMode"
                  value="active"
                  checked={employeeListMode === "active"}
                  onChange={() => setEmployeeListMode("active")}
                />
                <span>Employee List</span>
              </label>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="radio"
                  name="employeeListMode"
                  value="deleted"
                  checked={employeeListMode === "deleted"}
                  onChange={() => setEmployeeListMode("deleted")}
                />
                <span>Old Employee List</span>
              </label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
              {/* Search Field */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border border-gray-300 rounded-lg p-2 pl-9 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <Search className="w-5 h-5 absolute left-2 top-2.5 text-gray-400" />
              </div>

              {/* Department Filter */}
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.dname}
                  </option>
                ))}
              </select>

              {/* Designation/Role Filter */}
              <select
                value={selectedDesignation}
                onChange={(e) => setSelectedDesignation(e.target.value)}
                className="border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Designations</option>
                {designations.map((designation) => (
                  <option key={designation.id} value={designation.name}>
                    {designation.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto table-scroll">
            {employeeListMode === "deleted" ? (
              <table className="w-full table-auto min-w-max whitespace-nowrap">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="p-4 text-left">Emp No</th>
                    <th className="p-4 text-left">Name</th>
                    <th className="p-4 text-left">Email</th>
                    <th className="p-4 text-left">Deleted At</th>
                    <th className="p-4 text-left">Deletion Reason</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deletedEmployees.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="p-4 text-center text-gray-500">
                        No deleted employees found
                      </td>
                    </tr>
                  ) : (
                    deletedEmployees.map((d) => (
                      <tr key={d.id} className="border-t hover:bg-gray-50">
                        <td className="p-4">{d.empNo}</td>
                        <td className="p-4">{d.name}</td>
                        <td className="p-4">{d.email}</td>
                        <td className="p-4">{d.deleted_at ? formatDateDisplay(d.deleted_at) : "—"}</td>
                        <td className="p-4">{d.deletion_reason || "—"}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleRestoreDeletedEmployee(d.empNo)}
                              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              Restore
                            </button>
                            <button
                              type="button"
                              onClick={() => handleHardDeleteDeletedEmployee(d.id)}
                              className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="w-full table-auto min-w-max whitespace-nowrap">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-4 text-left">Emp No</th>
                  <th className="p-4 text-left">Name</th>
                  <th className="p-4 text-left">Email</th>
                  <th className="p-4 text-left">Mobile</th>
                  <th className="p-4 text-left">Department</th>
                  <th className="p-4 text-left">Designation</th>
                  <th className="p-4 text-left">Join Date</th>
                  <th className="p-4 text-left">Salary</th>
                  <th className="p-4 text-left">EPF Number</th>
                  <th className="p-4 text-left">Address</th>
                  <th className="p-4 text-left">Gender</th>
                  <th className="p-4 text-left">Marital Status</th>
                  <th className="p-4 text-left">Nationality</th>
                  <th className="p-4 text-left">Bank</th>
                  <th className="p-4 text-left">Bank Account</th>
                  <th className="p-4 text-left">Birthday</th>
                  <th className="p-4 text-left">Reports To</th>
                  <th className="p-4 text-left">NIC Number</th>
                  <th className="p-4 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td
                      colSpan="18"
                      className="p-4 text-center text-gray-500"
                    >
                      No employees found
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr
                      key={employee.id}
                      className="border-t hover:bg-gray-50"
                    >
                      <td className="p-4">
                        <button
                          className="text-blue-600 underline hover:text-blue-800"
                          onClick={() => navigate(`/employee-docs/${employee.empNo}`)}
                          title="View Documents"
                        >
                          {employee.empNo}
                        </button>
                      </td>
                      <td className="p-4">{employee.name}</td>
                      <td className="p-4">{employee.email}</td>
                      <td className="p-4">{employee.mobile || "—"}</td>
                      <td className="p-4">{employee.departmentName}</td>
                      <td className="p-4">{employee.designation}</td>
                      <td className="p-4">
                        {formatDateDisplay(employee.joinDate)}
                      </td>
                      <td className="p-4">{employee.salary || "—"}</td>
                      <td className="p-4">{employee.epfNumber || "—"}</td>
                      <td className="p-4">{employee.address || "—"}</td>
                      <td className="p-4">{employee.gender || "—"}</td>
                      <td className="p-4">
                        {employee.maritalStatus || "—"}
                      </td>
                      <td className="p-4">
                        {employee.nationalityCode || "—"}
                      </td>
                      <td className="p-4">{employee.bank || "—"}</td>
                      <td className="p-4">{employee.bankAccNo || "—"}</td>
                      <td className="p-4">
                        {formatDateDisplay(employee.birthday)}
                      </td>
                      <td className="p-4">
                        {employees.find(
                          (emp) => emp.id === Number(employee.reportsTo)
                        )?.name || "—"}
                      </td>
                      <td className="p-4">{employee.nicNumber || "—"}</td>
                      <td className="p-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(employee)}
                            className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                            title="Edit employee"
                          >
                            <Edit2 size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(employee.id)}
                            className="p-2 text-red-600 hover:text-red-800 transition-colors"
                            title="Delete employee"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateEmployeeAccount;